"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.arbitraryUserPrivateMetadata = arbitraryUserPrivateMetadata;
const fast_check_1 = require("fast-check");
const src_1 = require("../../src");
function arbitraryRole() {
    return (0, fast_check_1.oneof)((0, fast_check_1.constant)("ee"), (0, fast_check_1.constant)("product manager"));
}
function arbitraryPrimaryUse() {
    return (0, fast_check_1.oneof)((0, fast_check_1.constant)("simulation"), (0, fast_check_1.constant)("schematic"), (0, fast_check_1.constant)("pcb"));
}
function arbitraryReadyToStart() {
    return (0, fast_check_1.oneof)((0, fast_check_1.constant)("yes"), (0, fast_check_1.constant)("no"));
}
function arbitraryPersonalData() {
    return (0, fast_check_1.record)({
        role: arbitraryRole(),
        primaryUse: arbitraryPrimaryUse(),
        readyToStart: arbitraryReadyToStart(),
    });
}
function arbitraryProjectsData() {
    return (0, fast_check_1.record)({
        privateProjectCount: (0, fast_check_1.integer)({ min: 0 }),
    });
}
function arbitraryPlanName() {
    return (0, fast_check_1.oneof)((0, fast_check_1.constant)("educational"), (0, fast_check_1.constant)("pro"), (0, fast_check_1.constant)("free"));
}
function arbitrarySubscriptionItem() {
    return (0, fast_check_1.tuple)((0, fast_check_1.uuid)(), (0, fast_check_1.string)(), (0, fast_check_1.boolean)(), arbitraryPlanName()).map(([uid, price, active, planName]) => {
        return {
            uid,
            price: {
                uid: price,
                active,
                productUid: planName,
            },
        };
    });
}
function arbitraryPaymentSubscription() {
    const items = arbitrarySubscriptionItems();
    const uid = (0, fast_check_1.uuid)();
    const rand = (0, fast_check_1.float)();
    return (0, fast_check_1.tuple)(items, uid, rand).map(([items, uid, rando]) => {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        const subscriptionStatus = rando > 0.5 ? "created" : "deleted";
        const status = subscriptionStatus === "created" ? "active" : "canceled";
        return {
            uid,
            subscriptionStatus,
            status,
            items,
            metadata: {},
            cancelAt: null,
            trialEndsAt: null,
        };
    });
}
function arbitraryPaymentSubscriptions() {
    return (0, fast_check_1.uniqueArray)(arbitraryPaymentSubscription(), { comparator: (a, b) => a.uid !== b.uid }).map((subs) => {
        const result = {};
        for (const sub of subs) {
            result[sub.uid] = sub;
        }
        return result;
    });
}
function arbitraryPaymentData() {
    return (0, fast_check_1.record)({
        subscriptions: arbitraryPaymentSubscriptions(),
        customerUid: (0, fast_check_1.uuid)(),
    });
}
function arbitrarySubscriptionItems() {
    return (0, fast_check_1.uniqueArray)(arbitrarySubscriptionItem(), { comparator: (a, b) => a.uid !== b.uid }).map((items) => {
        const result = {};
        for (const item of items) {
            result[item.uid] = item;
        }
        return result;
    });
}
function arbitraryOnboardingData(constraints = {}) {
    return (0, fast_check_1.tuple)(constraints.onboarded !== undefined ? (0, fast_check_1.constant)(constraints.onboarded) : (0, fast_check_1.boolean)(), (0, fast_check_1.option)((0, fast_check_1.array)((0, fast_check_1.emailAddress)()))).map(([onboarded, invitedEmails]) => {
        return { onboarded, invitedEmails: invitedEmails ?? undefined };
    });
}
function arbitraryCreationFlags(constraints = {}) {
    const epoch = constraints.planEpoch
        ? (0, fast_check_1.constant)(constraints.planEpoch)
        : (0, fast_check_1.oneof)((0, fast_check_1.constant)(src_1.PlanEpoch.enum.legacy), (0, fast_check_1.constant)(src_1.PlanEpoch.enum.from_202410));
    const trialType = epoch.chain((planEpoch) => {
        return constraints.trialType
            ? (0, fast_check_1.constant)(constraints.trialType)
            : planEpoch === src_1.PlanEpoch.enum.legacy
                ? (0, fast_check_1.constant)(src_1.TrialType.enum.none)
                : (0, fast_check_1.oneof)((0, fast_check_1.constant)(src_1.TrialType.enum.payment_details_up_front), (0, fast_check_1.constant)(src_1.TrialType.enum.reverse));
    });
    const variant = epoch.chain((planEpoch) => {
        if (planEpoch === src_1.PlanEpoch.enum.legacy) {
            return (0, fast_check_1.constant)(undefined);
        }
        return constraints.planVariant
            ? (0, fast_check_1.constant)(constraints.planVariant)
            : (0, fast_check_1.oneof)((0, fast_check_1.constant)(undefined), (0, fast_check_1.constant)(src_1.PlanVariantForEpochFrom202410.enum.from_202410), (0, fast_check_1.constant)(src_1.PlanVariantForEpochFrom202410.enum.from_202504));
    });
    return (0, fast_check_1.record)({
        planEpoch: epoch,
        trialType,
        planVariant: variant,
    });
}
function arbitraryUserPrivateMetadata(constraints = {}) {
    const arbs = {
        uid: constraints.userUid ? (0, fast_check_1.constant)(constraints.userUid) : (0, fast_check_1.uuid)(),
        onboarding: arbitraryOnboardingData(constraints.onboarding ?? {}),
        personal: arbitraryPersonalData(),
        projects: arbitraryProjectsData(),
    };
    if (!constraints.noCreationFlags) {
        arbs.creationFlags = arbitraryCreationFlags(constraints.creationFlags ?? {});
    }
    if (!constraints.emptyPaymentDetails) {
        arbs.payment = arbitraryPaymentData();
    }
    return (0, fast_check_1.record)(arbs);
}
//# sourceMappingURL=userPrivateMetadata.js.map